import React, { useState, useEffect } from 'react';
import { X, Send, FileText } from 'lucide-react';
import axios from 'axios';

/**
 * MaterialOrderModal Component
 * 
 * A modal for creating and sending material orders to vendors based on the selection in the MaterialsSummary
 *
 * @param {Object} props 
 * @param {boolean} props.show - Whether to show the modal
 * @param {function} props.onClose - Function to close the modal
 * @param {Array} props.items - Material items to order
 * @param {function} props.onSuccess - Function to call after successful order creation
 */
const MaterialOrderModal = ({ show, onClose, items = [], onSuccess }) => {
  const [notes, setNotes] = useState("");
  const [groupedItems, setGroupedItems] = useState({});
  const [selectedVendor, setSelectedVendor] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  
  // Group items by vendor
  useEffect(() => {
    if (items.length === 0) return;
    
    const grouped = items.reduce((acc, item) => {
      const vendorName = item.vendorName || 'Unknown';
      
      if (!acc[vendorName]) {
        acc[vendorName] = {
          items: [],
          totalItems: 0
        };
      }
      
      acc[vendorName].items.push(item);
      acc[vendorName].totalItems += item.orderQuantity;
      
      return acc;
    }, {});
    
    setGroupedItems(grouped);
    
    // Set default vendor selection
    if (Object.keys(grouped).length > 0) {
      setSelectedVendor(Object.keys(grouped)[0]);
    }
  }, [items]);
  
  const handleVendorChange = (e) => {
    setSelectedVendor(e.target.value);
  };
  
  const handleSubmitOrder = async (e) => {
    e.preventDefault();
    
    if (!selectedVendor) {
      setError("Please select a vendor");
      return;
    }
    
    const vendorItems = groupedItems[selectedVendor]?.items || [];
    if (vendorItems.length === 0) {
      setError("No materials selected for this vendor");
      return;
    }
    
    setLoading(true);
    setError("");
    setSuccess("");
    
    try {
      // Prepare order data
      const orderData = {
        vendorName: selectedVendor,
        notes: notes,
        items: vendorItems.map(item => ({
          materialID: item.materialID,
          quantity: item.orderQuantity,
          materialName: item.name,
          sku: item.sku
        }))
      };
      
      // In a real implementation, you would send this to your API
      // const response = await axios.post('/api/material-orders', orderData);
      
      // For now, let's just simulate a successful response
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      // Set success message
      setSuccess(`Order for ${selectedVendor} created successfully!`);
      
      // Call success callback
      if (onSuccess) {
        onSuccess(orderData);
      }
      
      // Optionally reset form
      setNotes("");
      
    } catch (err) {
      console.error('Error creating material order:', err);
      setError(err.response?.data?.message || 'Failed to create order');
    } finally {
      setLoading(false);
    }
  };
  
  const generateOrderPDF = () => {
    // Implement PDF generation logic here
    alert("This would generate a PDF order for " + selectedVendor);
  };
  
  if (!show) return null;
  
  return (
    <>
      <div className="modal fade show d-block" tabIndex="-1">
        <div className="modal-dialog modal-lg">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">Generate Material Order</h5>
              <button
                type="button"
                className="btn-close"
                onClick={onClose}
              ></button>
            </div>
            <div className="modal-body">
              <form onSubmit={handleSubmitOrder}>
                {error && (
                  <div className="alert alert-danger alert-dismissible fade show">
                    {error}
                    <button 
                      type="button" 
                      className="btn-close" 
                      onClick={() => setError("")}
                    ></button>
                  </div>
                )}
                
                {success && (
                  <div className="alert alert-success alert-dismissible fade show">
                    {success}
                    <button 
                      type="button" 
                      className="btn-close" 
                      onClick={() => setSuccess("")}
                    ></button>
                  </div>
                )}
                
                <div className="mb-3">
                  <label className="form-label">Select Vendor</label>
                  <select 
                    className="form-select"
                    value={selectedVendor}
                    onChange={handleVendorChange}
                    required
                  >
                    <option value="">Choose a vendor...</option>
                    {Object.keys(groupedItems).map(vendor => (
                      <option key={vendor} value={vendor}>
                        {vendor} ({groupedItems[vendor].items.length} items)
                      </option>
                    ))}
                  </select>
                </div>
                
                {selectedVendor && (
                  <>
                    <div className="mb-3">
                      <h6>Items to Order from {selectedVendor}</h6>
                      <div className="table-responsive">
                        <table className="table table-sm table-bordered">
                          <thead className="table-light">
                            <tr>
                              <th>Material</th>
                              <th>SKU</th>
                              <th className="text-end">Quantity</th>
                              <th>Unit</th>
                            </tr>
                          </thead>
                          <tbody>
                            {groupedItems[selectedVendor].items.map(item => (
                              <tr key={`${item.materialType}-${item.materialID}`}>
                                <td>
                                  {item.name}
                                </td>
                                <td>{item.sku}</td>
                                <td className="text-end">{item.orderQuantity}</td>
                                <td>{item.unitOfMeasure}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                    
                    <div className="mb-3">
                      <label className="form-label">Order Notes</label>
                      <textarea
                        className="form-control"
                        rows="3"
                        placeholder="Enter any additional notes for this order..."
                        value={notes}
                        onChange={(e) => setNotes(e.target.value)}
                      ></textarea>
                    </div>
                  </>
                )}
                
                <div className="d-flex justify-content-between">
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={onClose}
                  >
                    <X size={16} className="me-1" />
                    Cancel
                  </button>
                  
                  <div className="btn-group">
                    <button
                      type="button"
                      className="btn btn-outline-primary"
                      onClick={generateOrderPDF}
                      disabled={!selectedVendor || loading}
                    >
                      <FileText size={16} className="me-1" />
                      Generate PDF
                    </button>
                    <button
                      type="submit"
                      className="btn btn-primary"
                      disabled={!selectedVendor || loading}
                    >
                      <Send size={16} className="me-1" />
                      {loading ? 'Sending...' : 'Send Order'}
                    </button>
                  </div>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show"></div>
    </>
  );
};

export default MaterialOrderModal;
